import React, { useMemo, useRef, useState } from 'react';
import useReport from '../lib/useReport';
import { useFilters } from '../contexts/FilterContext';
import { int, niceDate, toISODate, downloadCsv } from '../lib/format';
import { Card, Spinner, ErrorBox, Empty } from '../components/ui';

// Dough Planner — how many pizzas of each dough size to expect over the next
// days, per store (docs/dough-planner.md). Forward-looking, so it ignores the
// filter bar's date range and honours only the store picker.
//
// The server returns the expected number (forecast) and the typical range for
// each store × day × size. The safety buffer is applied HERE, so flipping it
// never refetches: prep = ceil(forecast × (1 + buffer)).

const BUFFERS = [0, 5, 10, 15, 20];
const BUFFER_KEY = 'sd-dough-buffer';

const readBuffer = () => {
  try {
    const raw = localStorage.getItem(BUFFER_KEY);
    // nothing saved must mean the default, not 0 — Number(null) is 0
    return raw !== null && BUFFERS.includes(Number(raw)) ? Number(raw) : 10;
  } catch { return 10; }
};

export const prep = (forecast, bufferPct) =>
  (forecast === null || forecast === undefined ? null : Math.ceil(forecast * (1 + bufferPct / 100) - 1e-9));

// "Sep 21" — the year is noise in a 7-day grid
const shortDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });

const signed = (n) => `${n > 0 ? '+' : ''}${n}%`;

// "Labour Day · +36%" — with the prior-year evidence on hover, so a manager
// can see WHY the number moved rather than having to trust it.
export function FactorPill({ factor, onExplain, open }) {
  if (!factor) return null;
  // Rendered as a button when the working can be opened (keyboard reachable).
  const Tag = onExplain ? 'button' : 'span';
  const act = onExplain ? { type: 'button', onClick: onExplain, 'aria-expanded': !!open,
    style: { marginLeft: 8, cursor: 'pointer', fontFamily: 'inherit', border: '1px solid transparent' } } : { style: { marginLeft: 8, border: '1px solid transparent' } };
  const hint = onExplain ? ' Click to see where this comes from.' : '';
  const vs = factor.basis === 'level' ? 'an average day' : 'a normal day of that weekday';
  if (factor.pct === null || factor.pct === undefined) {
    return (
      <Tag className="pill" {...act} style={{ ...act.style, border: '1px dashed var(--accent)', background: 'transparent' }}
        title={`${factor.name}: not enough history yet to adjust for it (needs two prior years). The number shown is the normal-day forecast — use your own judgement.${hint}`}>
        {factor.name} · not adjusted{onExplain ? (open ? ' ▾' : ' ▸') : ''}
      </Tag>
    );
  }
  const years = (factor.evidence || []).filter(e => e.used !== false);
  const history = years.length
    ? `Previous years vs ${vs}: ${years.map(e => `${e.year} ${signed(e.pct)}`).join(', ')}.`
    : 'Set manually for this site.';
  const what = factor.kind === 'season'
    ? `${factor.name}: this week usually runs ${signed(factor.pct)} against what recent weeks alone would suggest.`
    : `${factor.name}: usually ${signed(factor.pct)} vs ${vs}.`;
  return (
    <Tag className="pill" {...act} title={`${what} ${history} Already included in the numbers on this row.${hint}`}>
      {factor.name} · {signed(factor.pct)}{onExplain ? (open ? ' ▾' : ' ▸') : ''}
    </Tag>
  );
}

const PROVINCES = { AB: 'Alberta', BC: 'British Columbia', SK: 'Saskatchewan', MB: 'Manitoba', ON: 'Ontario', QC: 'Quebec',
  NB: 'New Brunswick', NS: 'Nova Scotia', PE: 'Prince Edward Island', NL: 'Newfoundland and Labrador', YT: 'Yukon', NT: 'Northwest Territories', NU: 'Nunavut' };
const provinceName = (code) => PROVINCES[String(code || '').replace(/^CA-/, '')] || code;
const longDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

// One history table: a row per prior year — that day, a normal day, and the
// difference. `counts` false = percentages only (chain rows for an owner).
function HistoryTable({ rows, unit, counts, season }) {
  if (!rows.length) return <div className="legend-note">No earlier years on record.</div>;
  return (
    <div className="table-wrap">
      <table className="data" style={{ fontSize: 12 }}>
        <thead>
          <tr>
            <th className="no-sort">{season ? 'Week of' : 'Date'}</th>
            {counts && <th className="num no-sort">{season ? `${unit} that week` : `${unit} that day`}</th>}
            {counts && <th className="num no-sort">{season ? 'Recent weeks suggested' : 'A normal day'}</th>}
            <th className="num no-sort">Difference</th>
            <th className="no-sort" />
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.year} style={r.used ? undefined : { color: 'var(--ink-muted)' }}>
              <td>{longDate(r.date)}</td>
              {counts && <td className="num">{int(r.actual)}</td>}
              {counts && <td className="num">{int(r.normal)}</td>}
              <td className="num" style={{ fontWeight: 600 }}>{signed(r.pct)}</td>
              <td style={{ color: 'var(--ink-muted)' }}>{r.used ? '' : 'not used'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// "Where does +58% come from?" — the working, chain first, then this store.
// Fetched on demand from /dough-factor. `locationId` adds the store's own
// history; without it (head-office view) only the chain side is shown.
export function FactorExplain({ day, locationId, region, storeName, buffer, onClose }) {
  const params = useMemo(() => ({ day, ...(locationId != null ? { locations: String(locationId) } : { region }) }), [day, locationId, region]);
  const { data, loading, error } = useReport('dough-factor', params);

  const box = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', whiteSpace: 'normal' };
  if (loading) return <div style={box}><Spinner label="Looking up previous years…" /></div>;
  if (error) return <div style={box}><ErrorBox message={error} /></div>;
  if (!data) return null;

  const f = data.factor;
  const season = f.kind === 'season';
  const level = f.basis === 'level';
  const used = data.chain.rows.filter(r => r.used);
  // A fixed-date holiday measured against its weekday, landing on a part of
  // the week it has rarely been seen on: say so, with the years that match.
  const partOfWeek = ['Fri', 'Sat'].includes(data.weekday) ? 'a Friday or Saturday' : data.weekday === 'Sun' ? 'a Sunday' : 'a Monday to Thursday';
  const alike = data.chain.rows.filter(r => r.sameClass);
  const rareLanding = !season && !level && f.anchored === 'date' && f.matched === 'all years' && f.pct !== null && alike.length < 2;
  const who = f.learnedFrom ? `stores in ${f.learnedFrom.map(provinceName).join(' and ')}` : 'the whole chain';
  const normalWord = season ? 'what the recent weeks alone would have suggested'
    : level ? 'an average day (the 8 weeks before it, every weekday, holidays left out)'
    : `a normal ${data.weekday} (the average of the 8 ${data.weekday}s before it, holidays left out)`;

  return (
    <div style={box} data-tour="dough-explain">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
        <div style={{ fontWeight: 600 }}>
          Where “{f.name} · {f.pct === null ? 'not adjusted' : signed(f.pct)}” comes from
        </div>
        {onClose && <button className="btn-ghost" onClick={onClose}>Close</button>}
      </div>

      <p className="legend-note" style={{ fontSize: 12.5, margin: '6px 0 10px' }}>
        {season
          ? `During the ${f.name.toLowerCase()} the recent weeks are a poor guide, so each year we check how this same week really compared with ${normalWord}.`
          : `Each year we compare ${f.name} with ${normalWord}.`}
        {' '}Measured across {who}{f.learnedFrom ? `, because only those provinces have this holiday` : ''} — one store on its own is too few days to learn from.
        {!season && level && ' This day has been steadier measured against an average day than against its weekday, so that is the yardstick used.'}
        {f.matched === 'same part of the week' && ` Only years when it fell on the same part of the week as this ${data.weekday} are used; the others are shown greyed.`}
      </p>

      {rareLanding && (
        <p className="legend-note" style={{ fontSize: 12.5, margin: '0 0 10px', color: 'var(--ink)' }}>
          <strong>Treat this one as a rough guide.</strong> This year {f.name} falls on {partOfWeek}, which is already a busy night, and it has
          {alike.length === 0 ? ' never landed there in the years on record' : ` only landed there once (${alike[0].year}: ${signed(alike[0].pct)})`}.
          {' '}The {signed(f.pct)} is the average of every year; busy nights tend to gain less on top.
        </p>
      )}
      <div className="grid" style={{ gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 12.5, marginBottom: 4 }}>{f.learnedFrom ? `Chain · ${f.learnedFrom.map(provinceName).join(' + ')}` : 'Whole chain'} · orders</div>
          <HistoryTable rows={data.chain.rows} unit="Orders" counts={!data.chain.countsHidden} season={season} />
          <div className="legend-note">
            {f.pct === null
              ? `Only ${used.length || data.chain.rows.length} earlier year${(used.length || data.chain.rows.length) === 1 ? '' : 's'} on record — two are needed before the plan is adjusted, so this day is planned as a normal ${data.weekday}.`
              : `Average of the ${used.length} year${used.length === 1 ? '' : 's'} used: ${signed(f.pct)} — applied to every store${f.learnedFrom ? ' in those provinces' : ''} and every dough size.`}
            {data.chain.countsHidden && ' Chain order counts are shown to head office only; the percentages are the same ones they see.'}
          </div>
        </div>

        {data.store && (
          <div>
            <div style={{ fontWeight: 600, fontSize: 12.5, marginBottom: 4 }}>{storeName || `Store ${data.store.locationId}`} · pizzas</div>
            <HistoryTable rows={data.store.rows} unit="Pizzas" counts season={season} />
            <div className="legend-note">
              {data.store.avgPct === null
                ? 'This store has no comparable earlier years yet, so the chain figure is the best guide there is.'
                : `This store's own average: ${signed(data.store.avgPct)} (chain: ${f.pct === null ? 'not yet set' : signed(f.pct)}). The plan uses the chain figure — a handful of days at one store swing too much to rely on — but if yours is consistently ${f.pct !== null && data.store.avgPct > f.pct ? 'higher' : 'different'}, allow for it with the safety buffer.`}
            </div>
          </div>
        )}
      </div>

      {data.store?.now && f.multiplier && (
        <div style={{ marginTop: 10, padding: '8px 10px', background: 'var(--accent-soft)', borderRadius: 6, fontSize: 12.5 }}>
          <strong>This {data.weekday}, {storeName || 'this store'}:</strong>{' '}
          {season ? 'recent weeks suggest' : level ? 'an average day is' : `a normal ${data.weekday} is`} {int(Math.round(data.store.now.normal))} pizzas
          {' '}× {f.multiplier.toFixed(2)} = <strong>{int(Math.round(data.store.now.expected))} expected</strong>
          {buffer ? `, then +${buffer}% safety buffer = ${int(prep(data.store.now.expected, buffer))}` : ''}.
          {' '}The grid works each dough size out the same way and rounds each one up, so its total can be a pizza or two higher.
        </div>
      )}
    </div>
  );
}

// Lowest–highest band with the expected value marked.
function RangeStrip({ low, high, value, max }) {
  if (!max || low === null || low === undefined) return null;
  const pos = v => `${Math.min(100, Math.max(0, (v / max) * 100))}%`;
  return (
    <div style={{ position: 'relative', height: 30, minWidth: 140 }}
      title={`Typically ${int(Math.round(low))}–${int(Math.round(high))}; expected ${int(Math.round(value))} (before safety buffer)`}>
      <div style={{ position: 'absolute', top: 12, height: 8, left: pos(low), width: `calc(${pos(high)} - ${pos(low)})`,
        background: 'var(--accent-soft)', border: '1px solid var(--accent)', borderRadius: 4, minWidth: 3 }} />
      <div style={{ position: 'absolute', top: 8, height: 16, width: 2, left: pos(value), background: 'var(--ink)' }} />
      <span style={{ position: 'absolute', top: -2, left: pos(low), transform: 'translateX(-50%)', fontSize: 10.5, color: 'var(--ink-muted)' }}>{int(Math.round(low))}</span>
      <span style={{ position: 'absolute', top: -2, left: pos(high), transform: 'translateX(-50%)', fontSize: 10.5, color: 'var(--ink-muted)' }}>{int(Math.round(high))}</span>
    </div>
  );
}

// One store, day by day.
export function StoreGrid({ store, plan, buffer, storeName }) {
  const [explain, setExplain] = useState(null); // day whose working is open
  const factors = plan.factors?.[store.region] || [];
  const maxCell = Math.max(1, ...store.days.flatMap(d => plan.sizes.map(s => d.sizes[s.key]?.forecast || 0)));
  const maxRange = Math.max(1, ...store.days.map(d => d.total?.high || 0)) * 1.05;
  const peak = store.days.reduce((best, d, i) => ((d.total?.forecast || 0) > (store.days[best]?.total?.forecast || 0) ? i : best), 0);
  const sums = plan.sizes.map(s => store.days.reduce((t, d) => t + (prep(d.sizes[s.key]?.forecast, buffer) || 0), 0));
  const rowTotal = d => plan.sizes.reduce((t, s) => t + (prep(d.sizes[s.key]?.forecast, buffer) || 0), 0);

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <tr>
            <th className="no-sort">Day</th>
            {plan.sizes.map(s => <th key={s.key} className="num no-sort">{s.label}</th>)}
            <th className="num no-sort">Total</th>
            <th className="no-sort" style={{ paddingLeft: 18 }}>Typical range, all sizes</th>
          </tr>
        </thead>
        <tbody>
          {store.days.map((d, i) => (
            <React.Fragment key={d.day}>
            <tr style={i === peak ? { fontWeight: 600 } : undefined}>
              <td style={{ whiteSpace: 'normal' }}>
                {plan.days[i].weekday} <span style={{ color: 'var(--ink-muted)', fontWeight: 400 }}>{shortDate(d.day)}</span>
                <FactorPill factor={factors[i]} open={explain === d.day}
                  onExplain={() => setExplain(explain === d.day ? null : d.day)} />
              </td>
              {plan.sizes.map(s => {
                const v = prep(d.sizes[s.key]?.forecast, buffer);
                return (
                  <td key={s.key} className="num" style={{ position: 'relative' }}>
                    {v !== null && (
                      <span style={{ position: 'absolute', left: 0, top: 6, bottom: 6, borderRadius: 3, opacity: 0.55,
                        width: `${Math.max(2, ((d.sizes[s.key].forecast) / maxCell) * 100)}%`, background: 'var(--accent-soft)' }} />
                    )}
                    <span style={{ position: 'relative' }}>{v === null ? '—' : int(v)}</span>
                  </td>
                );
              })}
              <td className="num">{int(rowTotal(d))}</td>
              <td style={{ paddingLeft: 18 }}>
                {d.total && <RangeStrip low={d.total.low} high={d.total.high} value={d.total.forecast} max={maxRange} />}
              </td>
            </tr>
            {explain === d.day && factors[i] && (
              <tr>
                <td colSpan={plan.sizes.length + 3} style={{ background: '#f7f6f2', padding: '10px 12px' }}>
                  <FactorExplain day={d.day} locationId={store.locationId} storeName={storeName} buffer={buffer} onClose={() => setExplain(null)} />
                </td>
              </tr>
            )}
            </React.Fragment>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ fontWeight: 600 }}>
            <td>{store.days.length} days</td>
            {sums.map((v, i) => <td key={plan.sizes[i].key} className="num">{int(v)}</td>)}
            <td className="num">{int(sums.reduce((a, b) => a + b, 0))}</td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function Accuracy({ lastWeek }) {
  if (!lastWeek || !lastWeek.actual) return null;
  const miss = Math.round((lastWeek.forecast / lastWeek.actual - 1) * 100);
  return (
    <span title="The same method, run for the most recent 7 days of real data using only what was known beforehand">
      Last {lastWeek.days} days: expected {int(lastWeek.forecast)}, sold {int(lastWeek.actual)} ({miss === 0 ? 'spot on' : `${signed(miss)} ${miss > 0 ? 'over' : 'under'}`})
    </span>
  );
}

export default function DoughPlannerPage() {
  const { reportParams, locations } = useFilters();
  const [buffer, setBufferState] = useState(readBuffer);
  const [horizon, setHorizon] = useState(7);
  const [focus, setFocus] = useState(null); // locationId drilled into from the all-stores table
  const [picked, setPicked] = useState(''); // a week the viewer chose; '' = from today
  const [explain, setExplain] = useState(null); // { day, region } open in the all-stores view

  const setBuffer = (b) => {
    setBufferState(b);
    try { localStorage.setItem(BUFFER_KEY, String(b)); } catch { /* private mode */ }
  };

  // `today` is the browser's today (see the cache note in doughController.js);
  // `start` only when the viewer picked a week of their own.
  const params = useMemo(() => ({
    today: toISODate(new Date()),
    ...(picked ? { start: picked } : {}),
    days: horizon,
    ...(reportParams.locations ? { locations: reportParams.locations } : {}),
  }), [horizon, picked, reportParams.locations]);
  const { data: fresh, loading, error } = useReport('dough-plan', params);
  // keep the last plan on screen while another week loads, so the picker and
  // the grid don't vanish under the viewer's cursor
  const lastPlan = useRef(null);
  if (fresh) lastPlan.current = fresh;
  const plan = fresh || lastPlan.current;

  const nameOf = (id) => {
    const l = locations.find(x => Number(x.ID) === Number(id));
    return l ? l.NAME : `Store ${id}`;
  };

  const controls = (
    <div className="filterbar">
      {plan && (
        <div className="date-inputs" data-tour="dough-week"
          title={`Plan any week from ${niceDate(plan.minStart)} to ${niceDate(plan.maxStart)} — for example the week of a holiday. Further out there is too little recent history to go on.`}>
          <span className="legend-note" style={{ margin: '0 6px 0 0', alignSelf: 'center' }}>Starting</span>
          <input
            type="date"
            aria-label="First day to plan"
            value={plan.start}
            min={plan.minStart}
            max={plan.maxStart}
            onChange={e => e.target.value && setPicked(e.target.value === plan.defaultStart ? '' : e.target.value)}
          />
          {picked && <button className="btn-ghost" onClick={() => setPicked('')}>↺ Back to now</button>}
        </div>
      )}
      <div className="chip-row" title="How many days ahead to plan">
        {[7, 14].map(n => (
          <button key={n} className={`chip ${horizon === n ? 'active' : ''}`} onClick={() => setHorizon(n)}>{n} days</button>
        ))}
      </div>
      <div className="chip-row" title="Extra dough on top of the expected number, to cover a busier-than-usual day">
        <span className="legend-note" style={{ margin: '0 2px 0 6px', alignSelf: 'center' }}>Safety buffer</span>
        {BUFFERS.map(b => (
          <button key={b} className={`chip ${buffer === b ? 'active' : ''}`} onClick={() => setBuffer(b)}>+{b}%</button>
        ))}
      </div>
    </div>
  );

  if (error) {
    return (
      <Card title="Dough Planner" actions={picked ? <button className="btn-ghost" onClick={() => setPicked('')}>↺ Back to now</button> : null}>
        <ErrorBox message={error} />
      </Card>
    );
  }
  if (!plan) return <Card title="Dough Planner"><Spinner label="Working out the dough…" /></Card>;
  if (!plan || plan.stores.length === 0) {
    return <Card title="Dough Planner"><Empty>Not enough sales history yet to plan dough for the selected stores.</Empty></Card>;
  }

  const stale = plan.staleDays || 0;
  const staleNote = stale >= 3
    ? `Sales data is ${stale} days behind (through ${niceDate(plan.dataThrough)}), so the most recent days are not reflected.`
    : null;

  const single = plan.stores.length === 1 ? plan.stores[0] : plan.stores.find(s => s.locationId === focus);
  // Adjusted days in view. Provinces can differ (a regional holiday, or just
  // its local name), so a day lists one label per distinct adjustment.
  const regions = Object.keys(plan.factors || {}).sort();
  const events = [];
  plan.days.forEach((d, i) => {
    const seen = new Map();
    for (const region of regions) {
      const f = plan.factors[region][i];
      if (!f) continue;
      const id = `${f.name}|${f.pct}`;
      if (!seen.has(id)) seen.set(id, { ...d, factor: f, region, regions: [] });
      seen.get(id).regions.push(region);
    }
    for (const e of seen.values()) events.push({ ...e, partial: e.regions.length < regions.length });
  });

  if (single) {
    return (
      <div className="grid" style={{ gap: 14, opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
        <Card
          tour="dough-grid"
          title={`Dough to prep · ${nameOf(single.locationId)}`}
          sub={`${niceDate(plan.days[0].day)} – ${niceDate(plan.days[plan.days.length - 1].day)} · pizzas by dough size, including pizzas inside combos`}
          actions={controls}
        >
          {plan.stores.length > 1 && (
            <button className="btn-ghost" style={{ marginBottom: 8 }} onClick={() => setFocus(null)}>← All stores</button>
          )}
          <StoreGrid store={single} plan={plan} buffer={buffer} storeName={nameOf(single.locationId)} />
          <div className="legend-note" style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
            <span>Numbers = expected pizzas{buffer ? ` + ${buffer}% buffer` : ''}, rounded up</span>
            <span>Band = lowest to highest of the last 8 same weekdays · mark = expected</span>
            <span>Bold = busiest day</span>
            <span>Click a holiday label to see where its percentage comes from</span>
            <Accuracy lastWeek={single.lastWeek} />
          </div>
          {staleNote && <div className="legend-note" style={{ color: 'var(--bad)' }}>{staleNote}</div>}
        </Card>
      </div>
    );
  }

  // Head-office view: one row per store, totals for the whole horizon.
  const rows = plan.stores.map(s => {
    const bySize = Object.fromEntries(plan.sizes.map(z => [z.key, s.days.reduce((t, d) => t + (prep(d.sizes[z.key]?.forecast, buffer) || 0), 0)]));
    const total = Object.values(bySize).reduce((a, b) => a + b, 0);
    const busiest = s.days.reduce((best, d) => ((d.total?.forecast || 0) > (best.total?.forecast || 0) ? d : best), s.days[0]);
    return { locationId: s.locationId, store: nameOf(s.locationId), bySize, total, busiest, lastWeek: s.lastWeek };
  }).sort((a, b) => b.total - a.total);
  const grand = plan.sizes.map(z => rows.reduce((t, r) => t + r.bySize[z.key], 0));

  const exportCsv = () => downloadCsv(`dough-plan-${plan.days[0].day}.csv`, [
    { key: 'store', label: 'Store' },
    ...plan.sizes.map(z => ({ key: z.key, label: z.label, csv: r => r.bySize[z.key] })),
    { key: 'total', label: 'Total' },
  ], rows);

  return (
    <div className="grid" style={{ gap: 14, opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
      <Card
        tour="dough-grid"
        title={`Dough to prep · ${rows.length} stores`}
        sub={`${niceDate(plan.days[0].day)} – ${niceDate(plan.days[plan.days.length - 1].day)} · ${plan.days.length}-day totals by dough size, including pizzas inside combos · click a store for its day-by-day plan`}
        actions={<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{controls}<button className="btn-ghost" onClick={exportCsv}>⬇ CSV</button></div>}
      >
        {events.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <span className="legend-note" style={{ marginRight: 4 }}>Adjusted for:</span>
            {events.map(d => {
              const open = explain && explain.day === d.day && explain.region === d.region;
              return (
                <span key={`${d.day}|${d.region}`} style={{ whiteSpace: 'nowrap' }}>
                  <span className="legend-note" style={{ marginLeft: 6 }}>
                    {d.weekday} {shortDate(d.day)}{d.partial ? ` (${d.regions.map(r => r.replace(/^CA-/, '')).join(', ')})` : ''}
                  </span>
                  <FactorPill factor={d.factor} open={open}
                    onExplain={() => setExplain(open ? null : { day: d.day, region: d.region })} />
                </span>
              );
            })}
            {explain && (
              <div style={{ marginTop: 8 }}>
                <FactorExplain day={explain.day} region={explain.region} onClose={() => setExplain(null)} />
                <div className="legend-note">Open a store below to see its own history for this day.</div>
              </div>
            )}
          </div>
        )}
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th className="no-sort">Store</th>
                {plan.sizes.map(z => <th key={z.key} className="num no-sort">{z.label}</th>)}
                <th className="num no-sort">Total</th>
                <th className="no-sort">Busiest day</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.locationId} onClick={() => setFocus(r.locationId)} style={{ cursor: 'pointer' }} title="Show this store day by day">
                  <td style={{ whiteSpace: 'normal' }}>{r.store}</td>
                  {plan.sizes.map(z => <td key={z.key} className="num">{int(r.bySize[z.key])}</td>)}
                  <td className="num" style={{ fontWeight: 600 }}>{int(r.total)}</td>
                  <td style={{ color: 'var(--ink-muted)' }}>
                    {plan.days.find(d => d.day === r.busiest.day)?.weekday} {shortDate(r.busiest.day)} · {int(plan.sizes.reduce((t, z) => t + (prep(r.busiest.sizes[z.key]?.forecast, buffer) || 0), 0))}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 600 }}>
                <td>All {rows.length} stores</td>
                {grand.map((v, i) => <td key={plan.sizes[i].key} className="num">{int(v)}</td>)}
                <td className="num">{int(grand.reduce((a, b) => a + b, 0))}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
        <div className="legend-note">
          Numbers = expected pizzas{buffer ? ` + ${buffer}% buffer` : ''}, rounded up per store per day, then added up.
        </div>
        {staleNote && <div className="legend-note" style={{ color: 'var(--bad)' }}>{staleNote}</div>}
      </Card>
    </div>
  );
}
